import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const DoNotSellModal = ({ isOpen, onClose }) => {
  const [optedOut, setOptedOut] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSaved(false);

    const loadPreference = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user) {
        const { data } = await supabase?.from('user_profiles')?.select('data_opt_out')?.eq('id', session?.user?.id)?.single();
        setOptedOut(data?.data_opt_out ?? false);
      } else {
        const stored = localStorage.getItem('data_opt_out');
        setOptedOut(stored === 'true');
      }
    };

    loadPreference();
  }, [isOpen]);

  const handleSave = async () => {
    setSaving(true);
    const { data: { session } } = await supabase?.auth?.getSession();

    if (session?.user) {
      await supabase?.from('user_profiles')?.update({ data_opt_out: optedOut })?.eq('id', session?.user?.id);
    }

    // Always keep localStorage in sync as fallback
    localStorage.setItem('data_opt_out', String(optedOut));

    setSaving(false);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      onClose();
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e?.target === e?.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Do Not Sell or Share My Personal Information
        </h2>
        <p className="text-sm text-gray-600 mb-6 leading-relaxed">
          Under applicable privacy laws (including CCPA/CPRA), you have the right to opt out of the sale or sharing of your personal information. Toggle the option below to set your preference.
        </p>

        <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4 mb-6">
          <div>
            <p className="text-sm font-semibold text-gray-800">Opt out of sale / sharing</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {optedOut ? 'Your data will not be sold or shared.' : 'Your data may be used for personalization and advertising.'}
            </p>
          </div>
          <button
            onClick={() => setOptedOut(!optedOut)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none ${optedOut ? 'bg-green-500' : 'bg-gray-300'}`}
            aria-pressed={optedOut}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${optedOut ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>

        {saved && (
          <div className="mb-4 text-center text-sm font-medium text-green-600">
            ✓ Your preference has been saved.
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #F06F6B 0%, #F58A54 100%)' }}
          >
            {saving ? 'Saving…' : 'Save Preference'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  const [showOptOutModal, setShowOptOutModal] = useState(false);

  const companyLinks = [
    { label: 'About Us', href: '/about' },
    { label: 'Press Room', href: '#' },
    { label: 'Blog', href: '#' },
  ];

  const legalLinks = [
    { label: 'Terms & Conditions', href: '/terms-and-conditions' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Cookie Policy', href: '/cookie-policy' },
    { label: 'Financial Product Disclosure', href: '/financial-disclosure' },
    { label: 'Affiliate Disclosure', href: '/affiliate-disclosure' },
  ];

  return (
    <>
      <DoNotSellModal isOpen={showOptOutModal} onClose={() => setShowOptOutModal(false)} />
      <footer
        style={{
          background: 'linear-gradient(135deg, #F06F6B 0%, #F07A5E 40%, #F58A54 100%)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">

            {/* Brand column */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <Link to="/coming-soon-landing">
                  <img
                    src="/assets/images/Stackd_logo_design_no_background_white_text-1772559212336.png"
                    alt="Stackd logo"
                    className="h-44 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                </Link>
                <span
                  className="text-2xl font-bold hidden text-white"
                >
                  Stackd
                </span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                The smarter way to compare vendors, manage projects, and make confident buying decisions.
              </p>
            </div>

            {/* Company column */}
            <div>
              <h3 className="font-bold text-xs uppercase tracking-widest mb-4 text-white opacity-70">
                Company
              </h3>
              <ul className="space-y-2">
                {companyLinks?.map((link) => (
                  <li key={link?.label}>
                    <Link
                      to={link?.href}
                      className="text-sm font-medium transition-all duration-200"
                      style={{ color: 'rgba(255,255,255,0.9)' }}
                      onMouseEnter={e => e.target.style.color = '#fff'}
                      onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.9)'}
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal column */}
            <div>
              <h3 className="font-bold text-xs uppercase tracking-widest mb-4 text-white opacity-70">
                Legal
              </h3>
              <ul className="space-y-2">
                {legalLinks?.map((link) => (
                  <li key={link?.label}>
                    <Link
                      to={link?.href}
                      className="text-sm font-medium transition-all duration-200 leading-snug block"
                      style={{ color: 'rgba(255,255,255,0.9)' }}
                      onMouseEnter={e => e.target.style.color = '#fff'}
                      onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.9)'}
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => setShowOptOutModal(true)}
                    className="text-sm font-medium transition-all duration-200 leading-snug block text-left"
                    style={{ color: 'rgba(255,255,255,0.9)' }}
                    onMouseEnter={e => e.target.style.color = '#fff'}
                    onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.9)'}
                  >
                    Do Not Sell or Share My Personal Information / Personal Data Opt-out
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div
            className="mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4"
            style={{ borderTop: '1px solid rgba(255,255,255,0.25)' }}
          >
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>
              &copy; {new Date()?.getFullYear()} Stackd. All rights reserved.
            </p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Built with ❤️ to simplify your buying journey.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
